import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import {
  createVenueAvailability,
  fetchVenueAvailability,
} from "./api";
import type { Club, VenueAvailabilityWindow } from "./types";
import DialogHeader from "./DialogHeader";
import {
  eachDateInRange,
  isValidTimeRange,
  normalizeVenueHireSettings,
  slotLengthFitsRange,
  toDateString,
  toTimeString,
  windowOverlapsExistingInRange,
} from "./venueHireUtils";

interface AddAvailabilityDialogProps {
  club: Club | null;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

const AddAvailabilityDialog: React.FC<AddAvailabilityDialogProps> = ({
  club,
  onClose,
  onSuccess,
  onError,
}) => {
  const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs());
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs());
  const [startTime, setStartTime] = useState<Dayjs | null>(
    dayjs().hour(9).minute(0),
  );
  const [endTime, setEndTime] = useState<Dayjs | null>(
    dayjs().hour(12).minute(0),
  );
  const [offer30Min, setOffer30Min] = useState(true);
  const [offer60Min, setOffer60Min] = useState(true);
  const [offer90Min, setOffer90Min] = useState(false);
  const [pricePer30Min, setPricePer30Min] = useState(8);
  const [pricePer60Min, setPricePer60Min] = useState(12);
  const [pricePer90Min, setPricePer90Min] = useState(18);
  const [existing, setExisting] = useState<VenueAvailabilityWindow[]>([]);
  const [validationError, setValidationError] = useState("");
  const [creating, setCreating] = useState(false);

  const dayCount = useMemo(() => {
    if (!fromDate || !toDate) return 1;
    const from = toDateString(fromDate);
    const to = toDateString(toDate.isBefore(fromDate) ? fromDate : toDate);
    return eachDateInRange(from, to).length;
  }, [fromDate, toDate]);

  useEffect(() => {
    if (!club) return;
    const settings = normalizeVenueHireSettings(club.venue_hire);
    setPricePer30Min(settings.price_per_30_min);
    setPricePer60Min(settings.price_per_60_min);
    setPricePer90Min(settings.price_per_90_min);
    setOffer30Min(settings.price_per_30_min > 0);
    setOffer60Min(settings.price_per_60_min > 0);
    setOffer90Min(settings.price_per_90_min > 0);
    setFromDate(dayjs());
    setToDate(dayjs());
    setStartTime(dayjs().hour(9).minute(0));
    setEndTime(dayjs().hour(12).minute(0));
    setValidationError("");
  }, [club]);

  useEffect(() => {
    if (!club || !fromDate || !toDate) return;

    const from = toDateString(fromDate);
    const to = toDateString(toDate.isBefore(fromDate) ? fromDate : toDate);

    const load = async () => {
      try {
        const data = await fetchVenueAvailability(club.id, from, to);
        setExisting(data);
      } catch {
        setExisting([]);
      }
    };
    load();
  }, [club, fromDate, toDate]);

  const validate = (): string | null => {
    if (!fromDate || !toDate || !startTime || !endTime) {
      return "Please select a date range and time range.";
    }
    if (toDate.isBefore(fromDate, "day")) {
      return "End date must be on or after the start date.";
    }
    if (!offer30Min && !offer60Min && !offer90Min) {
      return "Enable at least one slot length (30, 60, or 90 minutes).";
    }
    const fromStr = toDateString(fromDate);
    const toStr = toDateString(toDate);
    const startStr = toTimeString(startTime);
    const endStr = toTimeString(endTime);
    if (!isValidTimeRange(startStr, endStr)) {
      return "End time must be after start time.";
    }
    if (!slotLengthFitsRange(startStr, endStr, 30)) {
      return "The time range must divide evenly into 30-minute slots.";
    }
    if (offer60Min && !slotLengthFitsRange(startStr, endStr, 60)) {
      return "60-minute slots require a time range that divides evenly into 60 minutes.";
    }
    if (offer90Min && !slotLengthFitsRange(startStr, endStr, 90)) {
      return "90-minute slots require a time range that divides evenly into 90 minutes.";
    }
    if (offer30Min && pricePer30Min <= 0) {
      return "Enter a price greater than zero for 30-minute slots.";
    }
    if (offer60Min && pricePer60Min <= 0) {
      return "Enter a price greater than zero for 60-minute slots.";
    }
    if (offer90Min && pricePer90Min <= 0) {
      return "Enter a price greater than zero for 90-minute slots.";
    }
    const overlapDate = windowOverlapsExistingInRange(
      fromStr,
      toStr,
      startStr,
      endStr,
      existing,
    );
    if (overlapDate) {
      return `This window overlaps existing availability on ${dayjs(overlapDate).format("ddd D MMM YYYY")}.`;
    }
    return null;
  };

  const handleCreate = async () => {
    if (!club || !fromDate || !toDate || !startTime || !endTime) return;
    const error = validate();
    if (error) {
      setValidationError(error);
      return;
    }
    try {
      setCreating(true);
      const fromStr = toDateString(fromDate);
      const toStr = toDateString(toDate);
      await createVenueAvailability(club.id, {
        date: fromStr,
        end_date: fromStr === toStr ? undefined : toStr,
        start_time: toTimeString(startTime),
        end_time: toTimeString(endTime),
        price_per_30_min: offer30Min ? pricePer30Min : 0,
        price_per_60_min: offer60Min ? pricePer60Min : 0,
        price_per_90_min: offer90Min ? pricePer90Min : 0,
      });
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error creating availability:", err);
      const axiosErr = err as { response?: { data?: string } };
      onError(
        axiosErr.response?.data ||
          "Failed to add availability. Please try again.",
      );
    } finally {
      setCreating(false);
    }
  };

  const submitLabel =
    dayCount > 1
      ? creating
        ? `Adding ${dayCount} days…`
        : `Add ${dayCount} Days`
      : creating
        ? "Adding…"
        : "Add Availability";

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={club != null}
        onClose={() => !creating && onClose()}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogHeader
          title="Add Availability"
          subtitle={club?.name}
          onClose={onClose}
          closeDisabled={creating}
        />
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {validationError && (
              <Alert severity="error">{validationError}</Alert>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <DatePicker
                label="From date"
                value={fromDate}
                onChange={(value) => {
                  setFromDate(value);
                  if (value && toDate?.isBefore(value, "day")) {
                    setToDate(value);
                  }
                }}
                minDate={dayjs()}
                disabled={creating}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DatePicker
                label="To date"
                value={toDate}
                onChange={setToDate}
                minDate={fromDate ?? dayjs()}
                disabled={creating}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>
            {dayCount > 1 && (
              <Typography variant="body2" color="text.secondary">
                The same time window will be added for each of the {dayCount}{" "}
                days selected.
              </Typography>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TimePicker
                label="Start time"
                value={startTime}
                onChange={setStartTime}
                disabled={creating}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <TimePicker
                label="End time"
                value={endTime}
                onChange={setEndTime}
                disabled={creating}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Choose which slot lengths to offer and set a price for each.
            </Typography>

            <Stack spacing={1.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={offer30Min}
                    onChange={(e) => setOffer30Min(e.target.checked)}
                    disabled={creating}
                  />
                }
                label="Offer 30-minute slots"
              />
              {offer30Min && (
                <TextField
                  label="Price for 30-minute slot"
                  type="number"
                  value={pricePer30Min}
                  onChange={(e) =>
                    setPricePer30Min(Math.max(0, Number(e.target.value)))
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">£</InputAdornment>
                    ),
                  }}
                  inputProps={{ min: 0, step: 0.5 }}
                  disabled={creating}
                  fullWidth
                />
              )}
            </Stack>

            <Stack spacing={1.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={offer60Min}
                    onChange={(e) => setOffer60Min(e.target.checked)}
                    disabled={creating}
                  />
                }
                label="Offer 60-minute slots"
              />
              {offer60Min && (
                <TextField
                  label="Price for 60-minute slot"
                  type="number"
                  value={pricePer60Min}
                  onChange={(e) =>
                    setPricePer60Min(Math.max(0, Number(e.target.value)))
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">£</InputAdornment>
                    ),
                  }}
                  inputProps={{ min: 0, step: 0.5 }}
                  disabled={creating}
                  fullWidth
                />
              )}
            </Stack>

            <Stack spacing={1.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={offer90Min}
                    onChange={(e) => setOffer90Min(e.target.checked)}
                    disabled={creating}
                  />
                }
                label="Offer 90-minute slots"
              />
              {offer90Min && (
                <TextField
                  label="Price for 90-minute slot"
                  type="number"
                  value={pricePer90Min}
                  onChange={(e) =>
                    setPricePer90Min(Math.max(0, Number(e.target.value)))
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">£</InputAdornment>
                    ),
                  }}
                  inputProps={{ min: 0, step: 0.5 }}
                  disabled={creating}
                  fullWidth
                />
              )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : <Add />}
          >
            {submitLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddAvailabilityDialog;
